import React, { memo, useMemo } from 'react';
import { Company, User } from '@app/models';
import { fullName, getCompanyTypeShortName, setMask } from '@app/helpers';
import { IconCheckCircle24, IconHourGlass24 } from '@app/icons';

interface Props {
  company: Company | null;
  user: User | null;
  signed: boolean;
  signedText: string;
}

function Signer(props: Props) {
  const { signed, company, user, signedText } = props;

  const signerInfo = useMemo(() => {
    if (!!company) {
      return `${company.bin} - ${getCompanyTypeShortName(company.type)} ${
        company.name
      }`;
    }
    return `${user!.iin} - Физическое лицо`;
  }, [company, user]);

  const signerFullName = useMemo(() => {
    if (!!company) {
      return fullName(company.owner);
    }
    return fullName(user!);
  }, [company, user]);

  const signerPhone = useMemo(() => {
    if (!!company) {
      return setMask(company.owner.phone, '+7 (###) ### ## ##');
    }
    return setMask(user!.phone, '+7 (###) ### ## ##');
  }, [company, user]);

  return (
    <div className="w-full bg-background rounded-lg border border-border p-3 sm:p-4">
      <div className="flex flex-col gap-2 sm:gap-3 mb-3 sm:mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
          <div className="flex-1 min-w-0">
            <span className="block text-muted-foreground text-xs sm:text-sm font-medium leading-relaxed border-b border-dotted border-border pb-1 break-words">
              {signerInfo}
            </span>
          </div>
          <div
            className={`flex items-center gap-1.5 sm:gap-2 shrink-0 ${
              signed ? 'text-foreground' : 'text-muted-foreground'
            }`}
          >
            <span className="text-xs sm:text-sm font-medium whitespace-nowrap">
              {signed ? signedText : 'На подписании'}
            </span>
            {signed ? (
              <IconCheckCircle24 className="text-green-500 h-4 w-4 sm:h-5 sm:w-5" />
            ) : (
              <IconHourGlass24 className="text-muted-foreground h-4 w-4 sm:h-5 sm:w-5" />
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2 mb-3 sm:mb-4">
        <div className="flex items-center justify-between gap-2 sm:gap-3">
          <div className="flex-1 min-w-0">
            <span className="block text-muted-foreground text-xs sm:text-sm font-medium leading-relaxed border-b border-dotted border-border pb-1">
              Ф.И.О
            </span>
          </div>
          <span className="text-xs sm:text-sm font-medium text-foreground text-right break-words">
            {signerFullName}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2 sm:gap-3">
          <div className="flex-1 min-w-0">
            <span className="block text-muted-foreground text-xs sm:text-sm font-medium leading-relaxed border-b border-dotted border-border pb-1">
              Номер телефона
            </span>
          </div>
          <span className="text-xs sm:text-sm font-medium text-foreground whitespace-nowrap shrink-0">
            {signerPhone}
          </span>
        </div>
      </div>
    </div>
  );
}

export default memo(Signer);
